import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { profile, metrics, focusBullets, skills, services, projects as mockProjects, experience, education, contact, THEME } from "../mock";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { CheckCircle, ShieldCheck, FlaskConical, FileSpreadsheet, Workflow, LayoutGrid, Mail, Linkedin, Phone, Layers, CheckCheck } from "lucide-react";
import emailjs from "@emailjs/browser";
import { getProjects } from "../services/api";

const slugify = (t) => (t || "").toString().toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");

export function Hero() {
  const heroStats = metrics.map((m, i) => ({
    ...m,
    icon:
      i === 0 ? <Layers className="h-4 w-4 text-sky-300" /> :
      i === 1 ? <CheckCheck className="h-4 w-4 text-sky-300" /> :
      i === 2 ? <ShieldCheck className="h-4 w-4 text-sky-300" /> :
                <Workflow className="h-4 w-4 text-sky-300" />,
    isTherapeutic: i === 3 || /therapeutic/i.test(m.label),
  }));

  return (
    <section id="home" className={`${THEME.primaryBg} text-white`}>
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
            {profile.headline}
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{profile.subheadline}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href="#contact">
              <Button className={`${THEME.accent.bg} ${THEME.accent.hover} text-white w-full sm:w-auto`}>Request Consulting</Button>
            </a>
            <a href="#portfolio">
              <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800 w-full sm:w-auto">View Projects</Button>
            </a>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {heroStats.map((m) => (
              <div key={m.label} className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 p-4 hover:border-sky-500/40 transition-colors h-40 flex flex-col justify-between">
                <div className="flex items-center gap-2 text-slate-300">
                  {m.icon}
                  <span className="text-[11px] tracking-wide uppercase">{m.label}</span>
                </div>
                {m.isTherapeutic ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.value.split("•").map((chip) => (
                      <Badge key={chip.trim()} className="bg-white/10 text-slate-100 border-white/20 px-2 py-1 rounded-full text-[11px]">{chip.trim()}</Badge>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 text-white">
                    <div className="text-2xl font-bold leading-tight">{m.value}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 rounded-3xl bg-sky-600/20 blur-2xl"></div>
          <img src={profile.photo} alt="Rahul Uchit headshot" className="relative rounded-3xl w-full object-cover border border-slate-800 shadow-xl" />
        </div>
      </div>
    </section>
  );
}

export function About() {
  const handleDownloadCV = () => {
    const cvUrl = localStorage.getItem("cvUrl");
    if (cvUrl) {
      window.open(cvUrl, "_blank");
    } else {
      alert("CV not uploaded yet. Add it later and it will appear here automatically.");
    }
  };
  return (
    <section id="about" className="py-16 md:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">About Rahul</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">{profile.bio}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {focusBullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-sky-600 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={handleDownloadCV} className={`${THEME.accent.bg} ${THEME.accent.hover} text-white`}>Download CV (placeholder)</Button>
              <a href={contact.linkedin} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-slate-300 text-slate-800 hover:bg-slate-100">LinkedIn</Button>
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-2">Tip: later, set localStorage key "cvUrl" to your PDF URL and this button will open it.</p>
          </div>
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-slate-900">Certifications & Education</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="font-semibold">Education</div>
                <ul className="mt-2 text-sm text-slate-700 list-disc ml-5">
                  {education.education.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-semibold">Certifications</div>
                <ul className="mt-2 text-sm text-slate-700 list-disc ml-5">
                  {education.certifications.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

export function Skills() {
  return (
    <section id="skills" className="py-16 md:py-24 bg-slate-50">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Skills &amp; Expertise</h2>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((s) => (
            <Card key={s.title}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-900">{s.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">{s.desc}</CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Services() {
  const iconFor = (tag) => {
    switch (tag) {
      case "SDTM":
        return <FileSpreadsheet className="h-5 w-5 text-sky-600" />;
      case "Training":
        return <ShieldCheck className="h-5 w-5 text-sky-600" />;
      case "Local Lab":
        return <FlaskConical className="h-5 w-5 text-sky-600" />;
      default:
        return <Workflow className="h-5 w-5 text-sky-600" />;
    }
  };
  return (
    <section id="services" className="py-16 md:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex items-end justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Services</h2>
          <Badge className={`${THEME.accent.bg} text-white`}>Outcome-focused</Badge>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((svc) => (
            <Card key={svc.title} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2 flex-row items-center gap-3">
                {iconFor(svc.tag)}
                <CardTitle className="text-base text-slate-900">{svc.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">{svc.outcome}</CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Portfolio() {
  const { toast } = useToast();
  const [tab, setTab] = useState("All");
  const [items, setItems] = useState(mockProjects);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getProjects();
        if (mounted && Array.isArray(data) && data.length) {
          setItems(data);
        }
      } catch (e) {
        console.warn("Projects API failed, using mocks", e?.message || e);
        toast({ title: "Using mock projects", description: "Backend unreachable; showing placeholder projects.", duration: 3500 });
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(items.map((p) => p.category));
    return ["All", ...Array.from(cats)];
  }, [items]);

  const filtered = useMemo(() => {
    if (tab === "All") return items;
    return items.filter((p) => p.category === tab);
  }, [tab, items]);

  const placeholder = "https://images.unsplash.com/photo-1636990729669-70b389c7662a?auto=format&fit=crop&w=1200&q=60";

  return (
    <section id="portfolio" className="py-16 md:py-24 bg-slate-50">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Portfolio / Case Studies</h2>
          {loading && <span className="text-xs text-slate-500">Loading…</span>}
        </div>
        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="flex flex-wrap">
            {categories.map((c) => (
              <TabsTrigger key={c} value={c}>{c}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={tab} className="mt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => {
                const id = p.id || slugify(p.title);
                return (
                  <Card key={id} className="flex flex-col overflow-hidden">
                    <img src={p.thumbnail || placeholder} alt={p.title} className="h-40 w-full object-cover" />
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-slate-900 flex items-center gap-2">
                        <LayoutGrid className="h-4 w-4 text-sky-600" /> {p.title}
                      </CardTitle>
                      <div className="text-xs text-slate-500">{p.category}</div>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-2">
                      {p.problem && <div><span className="font-medium">Problem: </span>{p.problem}</div>}
                      {p.approach && <div><span className="font-medium">Approach: </span>{p.approach}</div>}
                      <div className="pt-2">
                        <Link to={`/projects/${id}`} className="text-sky-600 hover:text-sky-700 font-medium">View case study →</Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

export function Experience() {
  return (
    <section id="experience" className="py-16 md:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Experience</h2>
        <div className="mt-8 relative">
          <div className="absolute left-4 md:left-1/2 w-0.5 bg-slate-200 top-0 bottom-0"></div>
          <div className="space-y-8">
            {experience.map((e, idx) => (
              <div key={idx} className={`relative md:grid md:grid-cols-2 gap-8 items-start`}>
                <div className={`md:pl-0 ${idx % 2 === 0 ? "md:col-start-1" : "md:col-start-2"}`}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-slate-900">{e.role}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-700">
                      <div className="font-medium">{e.org}</div>
                      <div className="text-slate-500">{e.dates}</div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Education() {
  return (
    <section id="education" className="py-16 md:py-24 bg-slate-50">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Education &amp; Certifications</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-slate-900">Education</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-2">
              {education.education.map((e) => (
                <div key={e}>• {e}</div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-slate-900">Certifications</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-2">
              {education.certifications.map((e) => (
                <div key={e}>• {e}</div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const SERVICE_ID = "service_bxdouyk";
  const TEMPLATE_ID = "template_khrocdl";
  const PUBLIC_KEY = "9Fd2HCdPW2eqlB1v3";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const subject = `New contact from ${form.name} (${form.org || 'Independent'})`;

    const templateParams = {
      from_name: form.name,
      from_email: form.email,
      reply_to: form.email,
      organization: form.org || "N/A",
      message: form.message,
      subject,
      to_name: "Rahul Uchit",
      site: "Rahul Uchit Portfolio",
      user_name: form.name,
      user_email: form.email,
      user_organization: form.org || "N/A",
      user_message: form.message,
    };

    try {
      const res = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, { publicKey: PUBLIC_KEY });
      console.log("EmailJS sent:", res?.status, res?.text);
      toast({ title: "Thanks!", description: "Your message was sent successfully. I’ll reply within 1–2 business days." });
      setForm({ name: "", email: "", org: "", message: "" });
    } catch (err) {
      console.warn("EmailJS error", err);
      const submissions = JSON.parse(localStorage.getItem("contactSubmissions") || "[]");
      const payload = { ...form, id: Date.now() };
      localStorage.setItem("contactSubmissions", JSON.stringify([payload, ...submissions]));
      toast({
        title: "Sent later",
        description: "Email service unreachable now. Saved locally in this browser; please try again or contact via LinkedIn/email.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Contact</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-8 items-start">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-slate-900">Quick Contact</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-700">Name</label>
                  <Input required name="name" value={form.name} onChange={onChange} className={`${THEME.accent.ring}`} />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Work Email</label>
                  <Input required type="email" name="email" value={form.email} onChange={onChange} className={`${THEME.accent.ring}`} />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Organization</label>
                  <Input name="org" value={form.org} onChange={onChange} className={`${THEME.accent.ring}`} />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Message</label>
                  <Textarea required name="message" value={form.message} onChange={onChange} className={`${THEME.accent.ring}`} rows={4} />
                </div>
                <Button type="submit" disabled={sending} className={`${THEME.accent.bg} ${THEME.accent.hover} text-white`}>{sending ? "Sending..." : "Send"}</Button>
              </form>
              <div className="text-xs text-slate-500 mt-2">Emails are sent securely via EmailJS. In case of a network issue, your message is temporarily saved locally and you will see a notice above.</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-slate-900">Public Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-slate-700 text-sm">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-sky-600" /><a href={`mailto:${contact.email}`} className="underline decoration-sky-400/50 hover:decoration-sky-600">{contact.email}</a></div>
              <div className="flex items-center gap-2"><Linkedin className="h-4 w-4 text-sky-600" /><a href={contact.linkedin} target="_blank" rel="noreferrer" className="underline decoration-sky-400/50 hover:decoration-sky-600">LinkedIn</a></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-sky-600" /><span>Germany: {contact.phone_de}</span></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-sky-600" /><span>India: {contact.phone_in}</span></div>
              <div className="pt-2 text-xs text-slate-500">CTA placeholders: "Request Consulting" and "Book a 30-min Intro Call" can be linked later.</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}